import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const BLANK = { enabled: false, environment: "sandbox", network_id: "", branch_id: "", certificate_pem: "", private_key_pem: "" };

function Conn({ name, v, set, idLabel }) {
  const u = (k, val) => set({ ...v, [k]: val });
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`portal-conn-${name}`}>
      <div className="flex items-center gap-3"><Switch checked={!!v.enabled} onCheckedChange={(c) => u("enabled", c)} data-testid={`portal-${name}-enabled`} /><span className="font-bold capitalize">{name}</span></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1"><Label className="text-xs">Environment</Label>
          <Select value={v.environment} onValueChange={(x) => u("environment", x)}><SelectTrigger data-testid={`portal-${name}-env`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="sandbox">Test (sandbox)</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent></Select></div>
        {name === "rightmove" && <div className="space-y-1"><Label className="text-xs">Network ID</Label><Input value={v.network_id} onChange={(e) => u("network_id", e.target.value)} data-testid={`portal-${name}-network`} /></div>}
        <div className="space-y-1"><Label className="text-xs">{idLabel}</Label><Input value={v.branch_id} onChange={(e) => u("branch_id", e.target.value)} data-testid={`portal-${name}-branch`} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Certificate (PEM) {v.has_certificate && <span className="text-emerald-700">· saved</span>}</Label>
          <Textarea rows={3} className="font-mono text-xs" placeholder={v.has_certificate ? "Leave blank to keep" : "-----BEGIN CERTIFICATE-----"} value={v.certificate_pem || ""} onChange={(e) => u("certificate_pem", e.target.value)} data-testid={`portal-${name}-cert`} /></div>
        <div className="space-y-1"><Label className="text-xs">Private key (PEM) {v.has_private_key && <span className="text-emerald-700">· saved</span>}</Label>
          <Textarea rows={3} className="font-mono text-xs" placeholder={v.has_private_key ? "Leave blank to keep" : "-----BEGIN PRIVATE KEY-----"} value={v.private_key_pem || ""} onChange={(e) => u("private_key_pem", e.target.value)} data-testid={`portal-${name}-key`} /></div>
      </div>
    </div>
  );
}

export function PortalSettings({ disabled }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/biz/portal-settings").then((r) => setS({ rightmove: { ...BLANK, ...r.data.rightmove }, zoopla: { ...BLANK, ...r.data.zoopla } })).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const r = await api.put("/biz/portal-settings", s);
      setS({ rightmove: { ...BLANK, ...r.data.rightmove }, zoopla: { ...BLANK, ...r.data.zoopla } });
      toast.success("Portal connections saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel space-y-4 mb-6" data-testid="portal-settings">
      <div>
        <h3 className="text-xl font-bold">Portal connections</h3>
        <p className="text-sm text-slate-500 mt-1">Rightmove and Zoopla only accept listings through their approved feeds. Once they approve the feed for your branch, they'll give you a branch ID and a security certificate. Rightmove's comes as a keystore file, which needs converting to PEM text. Enter them below and switch the portal on. Until then, listings stay on your website only.</p>
      </div>
      <Conn name="rightmove" idLabel="Branch ID" v={s.rightmove} set={(v) => setS({ ...s, rightmove: v })} />
      <Conn name="zoopla" idLabel="Branch reference" v={s.zoopla} set={(v) => setS({ ...s, zoopla: v })} />
      <Button onClick={save} disabled={disabled} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="portal-settings-save">Save portal connections</Button>
    </div>
  );
}
